import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import PolicyDashboard from "./PolicyDashboard";
import MonthPicker from "../MonthPicker/index.js";

import {
  saveUpdateReimburse,
  initReimburseState,
} from "../../../../redux/slices/reimbursementSlice";
import {
  getReimbursementPayrollEmployeePath,
  calculateEarnedSalaryPath,
  addReimbursementStaffPath,
} from "../../../../services/config";

import { fetchBranchData, initBranchState } from "../../../../redux/slices/branchSlice";
import { fetchDepartmentData, initDepartState } from "../../../../redux/slices/departmentSlice";
import { fetchData, initShiftState } from "../../../../redux/slices/shiftsSlice";
import { commonSaveData, initCommonState } from "../../../../redux/slices/commonSlice";

import { HasPermission } from "../../../../services/hasPermission.js";
import {
  getInitials,
  getInitialColor,
  getCapitalizeWord,
  formatIndianAmountType,
} from "../../../../services/commonServices.js";
import {
  getPolicyGroups, getDefaultPolicyLimits, getPolicyChangeHistory,
  getExpenseTypes, getReimbursementStatuses,
} from '../../payrollDB';

// ─── Color tokens — use CSS variables so light/dark theme works ──────────────
const C = {
  bg:              "var(--pay-bg)",
  surface:         "var(--pay-surface)",
  surfaceSecondary:"var(--pay-surface-2)",
  border:          "var(--pay-border)",
  text:            "var(--pay-text)",
  muted:           "var(--pay-text-muted)",
  purple:          "#8660F8",
};

// ─── Inline style helpers ─────────────────────────────────────────────────────
const s = {
  input: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 12px",
    color: C.text,
    fontSize: 12,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 12px",
    color: C.text,
    fontSize: 12,
    outline: "none",
    appearance: "none",
    cursor: "pointer",
    paddingRight: 28,
  },
  btnPrimary: {
    background: C.purple,
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    color: "#fff",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    boxShadow: "0 0 20px rgba(134,96,248,0.25)",
  },
  btnOutline: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 16px",
    color: C.text,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  tabBtn: (active) => ({
    padding: "6px 16px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    border: active ? `1px solid rgba(134,96,248,0.2)` : "1px solid transparent",
    background: active ? "rgba(134,96,248,0.1)" : "transparent",
    color: active ? C.purple : C.muted,
  }),
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Pending:  { bg: "rgba(245,158,11,0.1)",  color: "#f59e0b", border: "rgba(245,158,11,0.2)",  dot: "#f59e0b" },
    Approved: { bg: "rgba(34,197,94,0.1)",   color: "#22c55e", border: "rgba(34,197,94,0.2)",   dot: "#22c55e" },
    Rejected: { bg: "rgba(239,68,68,0.1)",   color: "#ef4444", border: "rgba(239,68,68,0.2)",   dot: "#ef4444" },
    Review:   { bg: "rgba(59,130,246,0.1)",  color: "#3b82f6", border: "rgba(59,130,246,0.2)",  dot: "#3b82f6" },
    Draft:    { bg: "rgba(169,163,201,0.1)", color: C.muted,   border: "rgba(169,163,201,0.15)", dot: C.muted  },
  };
  const t = map[status] || map.Draft;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500, background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.dot, display: "inline-block", flexShrink: 0 }} />
      {status}
    </span>
  );
};

// ─── Policy Check Badge ───────────────────────────────────────────────────────
const PolicyBadge = ({ check, label, onClick }) => {
  if (!check) return <span style={{ color: C.muted, fontSize: 12 }}>—</span>;
  const map = {
    within:   { bg: "rgba(34,197,94,0.1)",  color: "#22c55e", border: "rgba(34,197,94,0.2)",  icon: "fa-circle-check",   text: "Within Limit"  },
    near:     { bg: "rgba(245,158,11,0.1)", color: "#f59e0b", border: "rgba(245,158,11,0.2)", icon: "fa-triangle-exclamation", text: label || "Near Limit" },
    exceeded: { bg: "rgba(239,68,68,0.1)",  color: "#ef4444", border: "rgba(239,68,68,0.2)",  icon: "fa-circle-xmark",   text: label || "Exceeded"   },
  };
  const t = map[check];
  if (!t) return null;
  return (
    <span onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500, background: t.bg, color: t.color, border: `1px solid ${t.border}`, cursor: onClick ? "pointer" : "default", whiteSpace: "nowrap" }}>
      <i className={`fa-solid ${t.icon}`} style={{ fontSize: 10 }} />
      {t.text}
    </span>
  );
};

// ─── Expense Type Cell ────────────────────────────────────────────────────────
const ExpenseTypeCell = ({ type }) => {
  const map = {
    Travel: { icon: "fa-plane",    color: C.purple    },
    Food:   { icon: "fa-utensils", color: "#22c55e"  },
    Fuel:   { icon: "fa-gas-pump", color: "#3b82f6"  },
    Other:  { icon: "fa-briefcase",color: "#a855f7"  },
  };
  const t = map[type] || map.Other;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <i className={`fa-solid ${t.icon}`} style={{ color: t.color, fontSize: 12 }} />
      <span style={{ color: C.text }}>{type || "Other"}</span>
    </div>
  );
};

// ─── Toggle Switch ────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <div onClick={onChange} style={{ width: 44, height: 24, borderRadius: 12, background: checked ? C.purple : C.surfaceSecondary, border: `1px solid ${checked ? C.purple : C.border}`, position: "relative", cursor: "pointer", transition: "all 0.2s", flexShrink: 0 }}>
    <div style={{ position: "absolute", top: 3, left: checked ? 22 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
  </div>
);

// ─── Category Limit Card ──────────────────────────────────────────────────────
const CategoryCard = ({ icon, label, iconBg, data, onChange }) => (
  <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{icon}</div>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{label}</span>
      </div>
      <Toggle checked={data.enabled} onChange={() => onChange("enabled", !data.enabled)} />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[
        { key: "monthly",   label: "Monthly Limit",         prefix: "₹", placeholder: ""          },
        { key: "perDay",    label: "Per Day Cap (Optional)", prefix: "",  placeholder: "No limit"  },
        { key: "maxClaims", label: "Max Claims/Month",       prefix: "",  placeholder: "Unlimited" },
      ].map(({ key, label: lbl, prefix, placeholder }) => (
        <div key={key}>
          <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{lbl}</label>
          <div style={{ position: "relative" }}>
            {prefix && <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: C.text, fontWeight: 600 }}>{prefix}</span>}
            <input value={data[key] || ""} onChange={e => onChange(key, e.target.value)} placeholder={placeholder}
              style={{ ...s.input, paddingLeft: prefix ? 22 : 12, fontSize: 14 }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Policy Limits Drawer ─────────────────────────────────────────────────────
const PolicyLimitsDrawer = ({ open, employee, onClose }) => {
  const [activeGroup, setActiveGroup] = useState("Engineers");
  const [policyActive, setPolicyActive] = useState(true);
  const [monthlyLimit, setMonthlyLimit] = useState(getDefaultPolicyLimits().monthlyLimit);
  const [categories, setCategories] = useState(getDefaultPolicyLimits().categories);

  const catConfig = [
    { key: "Travel", icon: "✈", iconBg: "rgba(59,130,246,0.1)"  },
    { key: "Food",   icon: "🍴", iconBg: "rgba(34,197,94,0.1)"  },
    { key: "Fuel",   icon: "⛽", iconBg: "rgba(245,158,11,0.1)" },
    { key: "Other",  icon: "💼", iconBg: "rgba(168,85,247,0.1)" },
  ];

  const groups = getPolicyGroups();

  const history = getPolicyChangeHistory();

  if (!open) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 999, backdropFilter: "blur(4px)" }} />
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1000, width: "min(95vw, 1200px)", background: C.bg, borderLeft: `1px solid ${C.border}`, display: "flex", flexDirection: "column", overflowY: "auto", boxShadow: "-20px 0 60px rgba(0,0,0,0.8)" }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "20px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: "0 0 4px" }}>
              <i className="fa-solid fa-shield-halved" style={{ marginRight: 10, color: C.purple }} />
              Policy Limits
            </h1>
            <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
              {employee ? `Policy for: ${employee.staffName || employee.employeeId}` : "Manage monthly caps, category limits, and exceptions"}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr 300px", gap: 20, padding: 24 }}>
          {/* Left panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14 }}>🔍</span>
              <input placeholder="Search policies..." style={{ ...s.input, paddingLeft: 40, background: C.surfaceSecondary, borderRadius: 12 }} />
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 14 }}>Policy Groups</div>
              {groups.map(g => {
                const isActive = activeGroup === g.label;
                return (
                  <button key={g.label} onClick={() => setActiveGroup(g.label)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "10px 14px", borderRadius: 10, border: isActive ? `1px solid ${C.purple}` : "1px solid transparent", background: isActive ? "rgba(134,96,248,0.12)" : "transparent", cursor: "pointer", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span>{g.icon}</span>
                      <span style={{ fontSize: 13, color: g.muted ? C.muted : C.text }}>{g.label}</span>
                    </div>
                    <span style={{ fontSize: 11, color: isActive ? C.purple : C.muted }}>{g.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Center panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Policy Information */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 20 }}>ℹ️ Policy Information</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div><label style={{ fontSize: 12, color: C.muted }}>Policy Name</label><input type="text" defaultValue="Engineer Standard Policy" style={{ ...s.input, marginTop: 6 }} /></div>
                <div><label style={{ fontSize: 12, color: C.muted }}>Applies To</label><select style={{ ...s.select, width: "100%", marginTop: 6 }}><option>Designation = Engineer</option></select></div>
                <div><label style={{ fontSize: 12, color: C.muted }}>Branch</label><select style={{ ...s.select, width: "100%", marginTop: 6 }}><option>All Branches</option></select></div>
                <div><label style={{ fontSize: 12, color: C.muted }}>Effective From</label><input type="date" defaultValue="2026-04-01" style={{ ...s.input, marginTop: 6 }} /></div>
              </div>
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div style={{ fontWeight: 600, fontSize: 14 }}>Policy Status</div><div style={{ fontSize: 12, color: C.muted }}>Toggle to activate</div></div>
                <Toggle checked={policyActive} onChange={() => setPolicyActive(!policyActive)} />
              </div>
            </div>

            {/* Monthly Cap */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 20 }}>📅 Monthly Employee Cap</h3>
              <label style={{ fontSize: 12, color: C.muted }}>Monthly Limit</label>
              <div style={{ position: "relative", marginTop: 6 }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}>₹</span>
                <input value={monthlyLimit} onChange={e => setMonthlyLimit(e.target.value)} style={{ ...s.input, paddingLeft: 32, borderColor: C.purple }} />
                <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: C.muted }}>per month</span>
              </div>
            </div>

            {/* Category Limits */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 20 }}>🗂️ Category Limits</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {catConfig.map(cat => (
                  <CategoryCard key={cat.key} icon={cat.icon} label={cat.key} iconBg={cat.iconBg}
                    data={categories[cat.key]}
                    onChange={(field, val) => setCategories(prev => ({ ...prev, [cat.key]: { ...prev[cat.key], [field]: val } }))}
                  />
                ))}
              </div>
            </div>

            {/* Change History */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>🕐 Change History</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {history.map((h, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.purple, marginTop: 5, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: C.text }}>{h.action}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{h.date} · {h.user}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>👁️ Policy Preview</h3>
              <div style={{ background: C.surfaceSecondary, borderRadius: 12, padding: 14, textAlign: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: C.muted }}>Monthly Max</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.purple }}>₹{monthlyLimit}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {catConfig.map(cat => (
                  <div key={cat.key} style={{ background: C.surfaceSecondary, borderRadius: 10, padding: 10, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: C.muted }}>{cat.key}</div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>₹{categories[cat.key].monthly}</div>
                  </div>
                ))}
              </div>
            </div>
            <button style={{ ...s.btnPrimary, width: "100%", padding: "14px", borderRadius: 12, justifyContent: "center" }}>💾 Save Changes</button>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Approval Popover ─────────────────────────────────────────────────────────
const ApprovalPopover = ({ open, anchorRect, amount, onConfirm, onClose }) => {
  const [showPartial, setShowPartial] = useState(false);
  const [partialAmt, setPartialAmt] = useState("");
  useEffect(() => { if (!open) { setShowPartial(false); setPartialAmt(""); } }, [open]);
  if (!open) return null;
  const top  = anchorRect ? Math.max(10, anchorRect.top - 200) : 100;
  const left = anchorRect ? Math.max(10, anchorRect.left - 260) : 100;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
      <div style={{ position: "fixed", top, left, zIndex: 50, background: C.surfaceSecondary, border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 12, padding: 16, minWidth: 260, boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>Approve Full Amount?</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.purple }}>₹{Number(amount).toLocaleString("en-IN")}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={() => { onConfirm(amount); onClose(); }} style={{ ...s.btnPrimary, background: "#22c55e", justifyContent: "center", width: "100%" }}>Confirm Approve</button>
          {!showPartial && (
            <button onClick={() => { setShowPartial(true); setPartialAmt(String(amount)); }} style={{ ...s.btnOutline, justifyContent: "center", width: "100%" }}>Approve Partial</button>
          )}
          <button onClick={onClose} style={{ ...s.btnOutline, justifyContent: "center", width: "100%", color: C.muted }}>Cancel</button>
        </div>
        {showPartial && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 6 }}>Approved Amount</label>
            <input type="number" value={partialAmt} onChange={e => setPartialAmt(e.target.value)} style={{ ...s.input, marginBottom: 8 }} placeholder="Enter amount" />
            <button onClick={() => { onConfirm(partialAmt); onClose(); }} style={{ ...s.btnPrimary, background: "#22c55e", justifyContent: "center", width: "100%" }}>Confirm Partial</button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Bulk Approve Modal ───────────────────────────────────────────────────────
const BulkModal = ({ open, count, totalAmount, onConfirm, onClose }) => {
  const [note, setNote]   = useState("");
  const [skip, setSkip]   = useState(false);
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 24 }}>
      <div style={{ background: C.surface, border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 14, padding: 24, maxWidth: 420, width: "100%", boxShadow: "0 0 20px rgba(134,96,248,0.15)" }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 16 }}>Approve {count} Claims?</h3>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>Total Amount</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: C.purple }}>₹{Number(totalAmount).toLocaleString("en-IN")}</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={skip} onChange={e => setSkip(e.target.checked)} style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 13, color: C.text }}>Skip claims exceeding policy</span>
          </label>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 8 }}>Approval Note (optional)</label>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note..." style={{ ...s.input, resize: "none" }} />
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => { onConfirm(note, skip); onClose(); }} style={{ ...s.btnPrimary, flex: 1, justifyContent: "center" }}>Confirm Bulk Approve</button>
          <button onClick={onClose} style={{ ...s.btnOutline, flex: 1, justifyContent: "center" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ─── Add Reimbursement Dialog ─────────────────────────────────────────────────
const AddReimburseDialog = ({ open, employee, reimburseStaff, onChange, onSave, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ style: { background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 14 } }}>
    <DialogTitle style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 20px" }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Add Reimbursement</span>
    </DialogTitle>
    <DialogContent style={{ padding: 20 }}>
      {employee && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: 10, background: "rgba(134,96,248,0.08)", borderRadius: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.purple, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700 }}>
            {getInitials?.(employee.staffName) || "?"}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{employee.staffName}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{employee.employeeId}</div>
          </div>
        </div>
      )}
      <input name="amount" value={reimburseStaff.amount} onChange={onChange} placeholder="Amount" style={{ ...s.input, marginBottom: 12 }} />
      <input name="paymentDate" type="date" value={reimburseStaff.paymentDate} onChange={onChange} style={{ ...s.input, marginBottom: 12 }} />
      <textarea name="notes" rows={3} value={reimburseStaff.notes} onChange={onChange} placeholder="Notes" style={{ ...s.input, resize: "none" }} />
      <button onClick={onSave} style={{ ...s.btnPrimary, width: "100%", justifyContent: "center", marginTop: 16 }}>Add</button>
    </DialogContent>
  </Dialog>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Reimbursement = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user_data")) || {};


// ← ADD THIS
const [rowStatuses, setRowStatuses] = useState({});
const [showPolicyDashboard, setShowPolicyDashboard] = useState(false);
  const branchState   = useSelector((st) => st.branch);
  const departState   = useSelector((st) => st.department);
  const shiftState    = useSelector((st) => st.shift);
  const reimburseStore = useSelector((st) => st.reimburse);
  const commonStore   = useSelector((st) => st.common);

  const [reimbursementList,    setReimbursementList]    = useState([]);
  const [reimbursementListOne, setReimbursementListOne] = useState([]);
  const [branch, setBranch]           = useState([]);
  const [shift,  setShift]            = useState([]);
  const [maxMonth, setMaxMonth]       = useState("");
  const [grandTotals, setGrandTotals] = useState({ totalCTC: 0, totalPayrollFinalized: 0, totalReimbursement: 0 });
  const [filterState, setFilterState] = useState({ payrollMonth: "", branch: "", shiftId: "", search: "", status: "All", type: "All" });
  const [activeTab,       setActiveTab]       = useState("All");
  const [selectedRows,    setSelectedRows]    = useState([]);
  const [policyDrawer,    setPolicyDrawer]    = useState({ open: false, employee: null });
  const [popover,         setPopover]         = useState({ open: false, amount: 0, anchorRect: null, rowId: null });
  const [bulkModal,       setBulkModal]       = useState(false);
  const [addPreReimburse, setAddPreReimburse] = useState({});
  const [addDialogOpen,   setAddDialogOpen]   = useState(false);
  const [reimburseStaff,  setReimburseStaff]  = useState({ userId: user.user_id, staffId: "", amount: "", paymentDate: "", notes: "" });
  const [currentPage,     setCurrentPage]     = useState(1);
  const PAGE_SIZE = 10;

  // ── Row statuses (local approval state) ──


  const getData = useCallback(() => {
    dispatch(fetchBranchData(user.user_id));
    dispatch(fetchDepartmentData(user.user_id));
    dispatch(fetchData(user.user_id));
  }, [dispatch, user.user_id]);

  useEffect(() => {
    const d  = new Date();
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setMaxMonth(ym);
    setFilterState(p => ({ ...p, payrollMonth: ym }));
  }, []);

  const getReimbursePayroll = () => {
    if (!filterState.payrollMonth) return;
    dispatch(commonSaveData({
      path: calculateEarnedSalaryPath,
      data: { userId: user.user_id, selectedDate: `${filterState.payrollMonth}-01`, branchId: filterState.branch || null, shiftId: filterState.shiftId || null },
    }));
  };

  useEffect(() => { if (filterState.payrollMonth) getReimbursePayroll(); }, [filterState.payrollMonth, filterState.branch, filterState.shiftId]);

  useEffect(() => {
    if (commonStore.commonSuccess && commonStore.commonData) {
      const list = commonStore.commonData?.staffDetails || [];
      setReimbursementList(list);
      setReimbursementListOne(list);
      getData();
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccess, commonStore.commonData, dispatch, getData]);

  // ── Grand totals ──
  useEffect(() => {
    const list = Array.isArray(reimbursementList) ? reimbursementList : [];
    const totalReimbursement = list.reduce((sum, r) => sum + Number(r.salaryCountDays?.reimburseAmount || 0), 0);
    setGrandTotals(prev => ({ ...prev, totalReimbursement }));
  }, [reimbursementList]);

  // ── Filtered display list ──
  const displayList = useMemo(() => {
    const base = Array.isArray(reimbursementList) ? reimbursementList : [];
    return base.filter(r => {
      const status = rowStatuses[r.staffId] || r.status || "Pending";
      const matchTab  = activeTab === "All" || status === activeTab;
      const matchSearch = !filterState.search ||
        (r.staffName || "").toLowerCase().includes(filterState.search.toLowerCase()) ||
        (r.employeeId || "").toLowerCase().includes(filterState.search.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [reimbursementList, activeTab, filterState.search, rowStatuses]);

  // ── Selection helpers ──
  const toggleSelect    = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedRows(selectedRows.length === displayList.length ? [] : displayList.map(r => r.staffId));
  const isAllSelected   = displayList.length > 0 && selectedRows.length === displayList.length;

  const selectedCount  = selectedRows.length;
  const selectedTotal  = displayList.filter(r => selectedRows.includes(r.staffId)).reduce((s, r) => s + Number(r.salaryCountDays?.reimburseAmount || 0), 0);

  // ── Approve row ──
  const approveRow = (staffId) => {
    setRowStatuses(prev => ({ ...prev, [staffId]: "Approved" }));
    toast.success("Claim approved!");
  };

  // ── Reject row ──
  const rejectRow = (staffId) => {
    setRowStatuses(prev => ({ ...prev, [staffId]: "Rejected" }));
    toast.info("Claim rejected.");
  };

  // ── Bulk approve ──
  const handleBulkApprove = () => {
    const updates = {};
    selectedRows.forEach(id => { updates[id] = "Approved"; });
    setRowStatuses(prev => ({ ...prev, ...updates }));
    setSelectedRows([]);
    toast.success(`${selectedCount} claims approved!`);
  };

  // ── Add reimburse ──
  const handleReimburseChange = (e) => setReimburseStaff(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleSaveReimburse   = () => {
    dispatch(saveUpdateReimburse({ path: addReimbursementStaffPath, data: reimburseStaff }));
    setAddDialogOpen(false);
  };

  const tabs = ["All", "Pending", "Approved", "Rejected", "Draft"];

  // ── Reset to page 1 on filter/tab change ──
  useEffect(() => { setCurrentPage(1); }, [activeTab, filterState.search]);

  // ── Pending count ──
  const pendingCount = useMemo(() => {
    return reimbursementList.filter(r => (rowStatuses[r.staffId] || r.status || "Pending") === "Pending").length;
  }, [reimbursementList, rowStatuses]);
  if (showPolicyDashboard) {
    return <PolicyDashboard onClose={() => setShowPolicyDashboard(false)} />;
  }

  return (
    <>
     
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" />
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Outfit', sans-serif; background: var(--pay-bg); color: var(--pay-text); }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: var(--pay-bg); }
        ::-webkit-scrollbar-thumb { background: rgba(134,96,248,0.2); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(134,96,248,0.4); }
        .glow-purple { box-shadow: 0 0 20px rgba(134,96,248,0.15); }
        .glow-green  { box-shadow: 0 0 15px rgba(34,197,94,0.2); }
        .row-hover:hover { background: rgba(22,16,50,0.5) !important; }
        @keyframes successFlash { 0%,100% { background-color: transparent; } 50% { background-color: rgba(34,197,94,0.1); } }
        .success-flash { animation: successFlash 0.8s ease-in-out; }
      `}</style>

      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Outfit', sans-serif", color: C.text }}>

        {/* ── FILTER / ACTION BAR ── */}
      {/* ── FILTER / ACTION BAR ── */}
<section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, margin: "20px 24px 0", padding: "10px 14px" }}>
  <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", justifyContent: "space-between", gap: 10 }}>

    {/* Left filters */}
    <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
      {/* Search — FIXED */}
      <div style={{ flex: "1 1 160px", minWidth: 0, position: "relative", display: "flex", alignItems: "center" }}>
        <i
          className="fa-solid fa-magnifying-glass"
          style={{
            position: "absolute",
            left: 9,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 11,
            color: C.muted,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <input
          type="text"
          placeholder="Search employee name / ID"
          value={filterState.search}
          onChange={e => setFilterState(p => ({ ...p, search: e.target.value }))}
          style={{
            ...s.input,
            width: "100%",
            boxSizing: "border-box",
            background: C.surfaceSecondary,
            fontSize: 11,
            padding: "6px 10px 6px 28px",
            height: 30,
          }}
        />
      </div>

      {/* Month — FIXED */}
      <div style={{ flex: "0 0 148px", minWidth: 0 }}>
        <MonthPicker
          value={filterState.payrollMonth}
          max={maxMonth}
          onChange={val => setFilterState(p => ({ ...p, payrollMonth: val }))}
        />
      </div>

      {/* Branch */}
      <div style={{ flex: "0 0 125px", minWidth: 0, position: "relative" }}>
        <select
          style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }}
          value={filterState.branch}
          onChange={e => setFilterState(p => ({ ...p, branch: e.target.value }))}>
          <option value="">All Branches</option>
          {(branchState?.branchData || []).map(b => <option key={b._id} value={b._id}>{b.branchName}</option>)}
        </select>
        <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
      </div>

      {/* Shift */}
      <div style={{ flex: "0 0 115px", minWidth: 0, position: "relative" }}>
        <select
          style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }}
          value={filterState.shiftId}
          onChange={e => setFilterState(p => ({ ...p, shiftId: e.target.value }))}>
          <option value="">All Shifts</option>
          {(shiftState?.shiftData || []).map(sh => <option key={sh._id} value={sh._id}>{sh.shiftName}</option>)}
        </select>
        <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
      </div>

      {/* Status */}
      <div style={{ flex: "0 0 115px", minWidth: 0, position: "relative" }}>
        <select
          style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }}>
          <option>All Status</option>
          <option>Pending</option>
          <option>Approved</option>
          <option>Rejected</option>
        </select>
        <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
      </div>

      {/* Type */}
      <div style={{ flex: "0 0 110px", minWidth: 0, position: "relative" }}>
        <select
          style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }}>
          <option>All Types</option>
          <option>Travel</option>
          <option>Food</option>
          <option>Fuel</option>
          <option>Other</option>
        </select>
        <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
      </div>

    </div>

    {/* Right actions */}
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
      <div style={{ position: "relative" }}>
        <select
          disabled={selectedCount === 0}
          style={{
            ...s.select,
            background: selectedCount > 0 ? C.surfaceSecondary : "rgba(22,16,50,0.5)",
            color: selectedCount > 0 ? C.text : C.muted,
            cursor: selectedCount > 0 ? "pointer" : "not-allowed",
            height: 30,
            fontSize: 11,
            padding: "6px 22px 6px 10px",
            boxSizing: "border-box",
          }}
          onChange={e => { if (e.target.value === "approve") setBulkModal(true); }}>
          <option>Bulk Actions</option>
          <option value="approve">Approve Selected</option>
          <option value="reject">Reject Selected</option>
        </select>
        <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
      </div>

      <button onClick={() => setShowPolicyDashboard(true)} style={{ ...s.btnOutline, height: 30, padding: "0 12px", fontSize: 11 }}>
        <i className="fa-solid fa-shield-halved" style={{ fontSize: 11 }} /> Policy Dashboard
      </button>

      <button style={{ ...s.btnOutline, height: 30, padding: "0 12px", fontSize: 11 }}>
        <i className="fa-solid fa-download" style={{ fontSize: 11 }} /> Export
      </button>

      <button onClick={() => setAddDialogOpen(true)} style={{ ...s.btnPrimary, height: 30, padding: "0 12px", fontSize: 11, boxShadow: "0 0 20px rgba(134,96,248,0.25)" }}>
        <i className="fa-solid fa-plus" style={{ fontSize: 11 }} /> New Claim
      </button>
    </div>

  </div>
</section>

        {/* ── KPI CARDS ── */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, padding: "16px 24px 0" }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(134,96,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
              <i className="fa-solid fa-receipt" style={{ color: C.purple }} />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Total Claims This Month</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>₹{grandTotals.totalReimbursement.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>+12% from last month</div>
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
              <i className="fa-solid fa-clock" style={{ color: "#f59e0b" }} />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Pending Approval</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{pendingCount} Claims</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Avg. wait: 2.4 days</div>
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(34,197,94,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
              <i className="fa-solid fa-circle-check" style={{ color: "#22c55e" }} />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Approved This Month</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              ₹{reimbursementList.filter(r => (rowStatuses[r.staffId] || r.status) === "Approved")
                .reduce((s, r) => s + Number(r.salaryCountDays?.reimburseAmount || 0), 0).toLocaleString("en-IN")}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {reimbursementList.filter(r => (rowStatuses[r.staffId] || r.status) === "Approved").length} claims processed
            </div>
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(59,130,246,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
              <i className="fa-solid fa-shield-halved" style={{ color: "#3b82f6" }} />
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Policy Limits Active</div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{reimbursementList.length} Employees</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>12 rules updated this month</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Travel, Food, Fuel, Other Caps Applied</div>
          </div>
        </section>

        {/* ── MAIN BODY ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, padding: "16px 24px 24px" }}>

          {/* LEFT — claims table */}
          <div>
            {/* Bulk action bar */}
            {selectedCount > 0 && (
              <div style={{ background: "rgba(134,96,248,0.1)", border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 14, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 0 20px rgba(134,96,248,0.1)" }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{selectedCount} Claims Selected</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setBulkModal(true)} style={{ ...s.btnPrimary, fontSize: 12, padding: "6px 14px" }}>
                    <i className="fa-solid fa-check" style={{ fontSize: 11 }} /> Approve Selected
                  </button>
                  <button onClick={() => { selectedRows.forEach(id => rejectRow(id)); setSelectedRows([]); }} style={{ padding: "6px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#ef4444", cursor: "pointer" }}>
                    <i className="fa-solid fa-xmark" style={{ fontSize: 11, marginRight: 4 }} /> Reject Selected
                  </button>
                  <button style={{ padding: "6px 14px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#f59e0b", cursor: "pointer" }}>
                    <i className="fa-solid fa-pause" style={{ fontSize: 11, marginRight: 4 }} /> Hold Selected
                  </button>
                  <button style={{ ...s.btnOutline, fontSize: 12, padding: "6px 14px" }}>
                    <i className="fa-solid fa-download" style={{ fontSize: 12}} /> Export Selected
                  </button>
                  <button onClick={() => setSelectedRows([])} style={{ ...s.btnOutline, fontSize: 12, padding: "6px 14px", color: C.muted }}>
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            {/* Table card */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
                <h2 style={{ fontSize: 12, fontWeight: 600, margin: "0 0 14px" }}>Reimbursement Claims</h2>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {tabs.map(t => (
                    <button key={t} onClick={() => setActiveTab(t)} style={s.tabBtn(activeTab === t)}>{t}</button>
                  ))}
                </div>
              </div>

             <div style={{ overflowX: "auto" }}>
  {/* CSS Grid Table Header */}
  <div style={{
    display: "grid",
    gridTemplateColumns: "40px minmax(160px, 1.2fr) minmax(90px, 0.7fr) minmax(100px, 0.7fr) minmax(80px, 0.6fr) minmax(100px, 0.8fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(130px, 1fr)",
    background: "rgba(22,16,50,0.5)",
    borderBottom: `1px solid ${C.border}`,
    padding: "8px 12px",
    fontSize: 10,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: C.muted,
    position: "sticky",
    top: 0,
    zIndex: 10
  }}>
    <div style={{ textAlign: "center" }}>
      <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} style={{ cursor: "pointer", width: 14, height: 14 }} />
    </div>
    <div>Employee</div>
    <div>Dept</div>
    <div>Expense Type</div>
    <div>Amount</div>
    <div>Policy Check</div>
    <div>Submitted</div>
    <div>Status</div>
    <div style={{ textAlign: "right" }}>Actions</div>
  </div>

  {/* Grid Body */}
  <div style={{ minHeight: 400 }}>
    {displayList.length === 0 ? (
      <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>
        No claims found
      </div>
    ) : displayList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((row, i) => {
      const isSelected = selectedRows.includes(row.staffId);
      const rowStatus = rowStatuses[row.staffId] || row.status || "Pending";
      const amount = Number(row.salaryCountDays?.reimburseAmount || 0);
      const isPending = rowStatus === "Pending" || rowStatus === "Review";

      return (
        <div
          key={i}
          className="row-hover"
          style={{
            display: "grid",
            gridTemplateColumns: "40px minmax(160px, 1.2fr) minmax(90px, 0.7fr) minmax(100px, 0.7fr) minmax(80px, 0.6fr) minmax(100px, 0.8fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(130px, 1fr)",
            padding: "8px 12px",
            borderBottom: `1px solid ${C.border}`,
            background: isSelected ? "rgba(134,96,248,0.06)" : "transparent",
            transition: "background 0.15s",
            alignItems: "center"
          }}
        >
          {/* Checkbox */}
          <div style={{ textAlign: "center" }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(row.staffId)}
              style={{ cursor: "pointer", width: 14, height: 14 }}
            />
          </div>

          {/* Employee */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: C.purple,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 10,
                fontWeight: 600,
                flexShrink: 0
              }}>
                {getInitials?.(row.staffName) || "?"}
              </div>
              <div>
                <div style={{ fontWeight: 500, fontSize: 11 }}>{row.staffName || "—"}</div>
                <div style={{ fontSize: 9, color: C.muted }}>{row.employeeId}</div>
              </div>
            </div>
          </div>

          {/* Department */}
          <div style={{ fontSize: 11, color: C.muted }}>{row.department || "—"}</div>

          {/* Expense type */}
          <div>
            <ExpenseTypeCell type={row.expenseType || "Other"} />
          </div>

          {/* Amount */}
          <div style={{ fontWeight: 600, fontSize: 12 }}>
            ₹{amount.toLocaleString("en-IN")}
          </div>

          {/* Policy check */}
          <div>
            <PolicyBadge
              check={row.policyCheck || "within"}
              label={row.policyLabel}
              onClick={() => navigate("/policy-limits", { state: { employee: row } })}
            />
          </div>

          {/* Submitted date */}
          <div style={{ color: C.muted, fontSize: 10, whiteSpace: "nowrap" }}>
            {row.submittedDate || row.paymentDate || "—"}
          </div>

          {/* Status */}
          <div>
            <StatusBadge status={rowStatus} />
          </div>

          {/* Actions */}
          <div style={{ textAlign: "right" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
           <button 
  onClick={() => navigate('/claim-details', { state: { claim: row } })}
  style={{
    padding: "3px 8px",
    fontSize: 10,
    fontWeight: 500,
    color: C.muted,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    background: "transparent",
    cursor: "pointer"
  }}>
  View
</button>
              {isPending && (
                <>
                  <button
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setPopover({ open: true, amount, anchorRect: rect, rowId: row.staffId });
                    }}
                    style={{
                      padding: "3px 8px",
                      fontSize: 10,
                      fontWeight: 500,
                      color: "#fff",
                      background: "rgba(34,197,94,0.9)",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer"
                    }}>
                    Approve
                  </button>
                  <button
                    onClick={() => rejectRow(row.staffId)}
                    style={{
                      padding: "3px 8px",
                      fontSize: 10,
                      fontWeight: 500,
                      color: "#ef4444",
                      border: "1px solid rgba(239,68,68,0.3)",
                      borderRadius: 6,
                      background: "transparent",
                      cursor: "pointer"
                    }}>
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>

  {/* Pagination row */}
  <div style={{
    padding: "8px 12px",
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }}>
    <span style={{ fontSize: 11, color: C.muted }}>
      Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, displayList.length)}–{Math.min(currentPage * PAGE_SIZE, displayList.length)} of {displayList.length} claims
    </span>
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      {/* Prev */}
      <button
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(p => p - 1)}
        style={{
          width: 28, height: 28, borderRadius: 6,
          border: `1px solid ${C.border}`,
          background: C.surfaceSecondary,
          color: currentPage === 1 ? C.border : C.muted,
          cursor: currentPage === 1 ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: 10 }} />
      </button>

      {/* Page numbers */}
      {(() => {
        const totalPages = Math.max(1, Math.ceil(displayList.length / PAGE_SIZE));
        const pages = [];
        let start = Math.max(1, currentPage - 1);
        let end   = Math.min(totalPages, start + 2);
        if (end - start < 2) start = Math.max(1, end - 2);
        for (let p = start; p <= end; p++) pages.push(p);
        return pages.map(p => (
          <button
            key={p}
            onClick={() => setCurrentPage(p)}
            style={{
              width: 28, height: 28, borderRadius: 6,
              border: p === currentPage ? "none" : `1px solid ${C.border}`,
              background: p === currentPage ? C.purple : C.surfaceSecondary,
              color: p === currentPage ? "#fff" : C.muted,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: p === currentPage ? 600 : 400,
            }}>
            {p}
          </button>
        ));
      })()}

      {/* Next */}
      <button
        disabled={currentPage >= Math.ceil(displayList.length / PAGE_SIZE)}
        onClick={() => setCurrentPage(p => p + 1)}
        style={{
          width: 28, height: 28, borderRadius: 6,
          border: `1px solid ${C.border}`,
          background: C.surfaceSecondary,
          color: currentPage >= Math.ceil(displayList.length / PAGE_SIZE) ? C.border : C.muted,
          cursor: currentPage >= Math.ceil(displayList.length / PAGE_SIZE) ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
        <i className="fa-solid fa-chevron-right" style={{ fontSize: 10 }} />
      </button>
    </div>
  </div>
</div>
            </div>
          </div>

          {/* RIGHT — insight cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Today Pending Actions */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Today Pending Actions</h3>
              {[
                { label: "Pending Claims",    value: pendingCount,   color: C.text     },
                { label: "Selected",          value: selectedCount,  color: C.purple   },
                { label: "Ready to Approve",  value: Math.max(0, pendingCount - 2), color: "#22c55e" },
                { label: "Need Review",       value: 2,              color: "#f59e0b"  },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
                  <span style={{ fontSize: 12, color: C.muted }}>{item.label}</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}</span>
                </div>
              ))}
            </div>

            {/* Policy Matrix */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Reimbursement Policy Matrix</h3>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}`, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: C.muted }}>Designation</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>Monthly Cap</span>
              </div>
              {[
                { role: "Executive",       cap: "₹3,000"  },
                { role: "Engineer",        cap: "₹5,000"  },
                { role: "Manager",         cap: "₹8,000"  },
                { role: "Senior Manager",  cap: "₹12,000" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
                  <span style={{ fontSize: 12, color: C.text }}>{r.role}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.purple }}>{r.cap}</span>
                </div>
              ))}
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>Category Limits</div>
                {[
                  { icon: "fa-plane",    color: C.purple,  label: "Travel", cap: "₹2,500/month" },
                  { icon: "fa-utensils", color: "#22c55e", label: "Food",   cap: "₹1,500/month" },
                  { icon: "fa-gas-pump", color: "#3b82f6", label: "Fuel",   cap: "₹3,000/month" },
                  { icon: "fa-briefcase",color: "#a855f7", label: "Other",  cap: "₹1,000/month" },
                ].map((c, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <i className={`fa-solid ${c.icon}`} style={{ color: c.color, fontSize: 12 }} />
                      <span style={{ fontSize: 12 }}>{c.label}</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>{c.cap}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
                <p style={{ fontSize: 10, color: C.muted, textAlign: "center", margin: 0 }}>Limits auto-check during claim submission</p>
              </div>
            </div>

            {/* Policy Alerts */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Policy Alerts</h3>
              {[
                { icon: "fa-triangle-exclamation", color: "#f59e0b", bg: "rgba(245,158,11,0.05)",  border: "rgba(245,158,11,0.2)",  label: "Near monthly limit",    count: 7 },
                { icon: "fa-circle-exclamation",   color: "#ef4444", bg: "rgba(239,68,68,0.05)",   border: "rgba(239,68,68,0.2)",   label: "Exceeded category cap", count: 3 },
                { icon: "fa-shield-halved",         color: "#3b82f6", bg: "rgba(59,130,246,0.05)",  border: "rgba(59,130,246,0.2)",  label: "Approval exceptions",   count: 2 },
              ].map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: a.bg, border: `1px solid ${a.border}`, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ color: a.color, fontSize: 12 }} />
                    <span style={{ fontSize: 12, color: C.text }}>{a.label}</span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: a.color }}>{a.count}</span>
                </div>
              ))}
            </div>

            {/* Category Breakdown */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Category Breakdown</h3>
              {[
                { label: "Travel", amount: "₹52k", pct: 35, color: C.purple    },
                { label: "Food",   amount: "₹24k", pct: 16, color: "#22c55e"  },
                { label: "Fuel",   amount: "₹18k", pct: 12, color: "#3b82f6"  },
                { label: "Other",  amount: "₹11k", pct: 7,  color: "#a855f7"  },
              ].map((c, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: C.muted }}>{c.label}</span>
                    <span style={{ fontWeight: 600 }}>{c.amount}</span>
                  </div>
                  <div style={{ width: "100%", background: C.surfaceSecondary, height: 6, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${c.pct}%`, height: "100%", borderRadius: 3, background: c.color }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Approval Alerts */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Approval Alerts</h3>
              {[
                { icon: "fa-clock",   color: "#ef4444", bg: "rgba(239,68,68,0.05)",  border: "rgba(239,68,68,0.2)",  label: "Pending > 3 Days",     count: 5 },
                { icon: "fa-receipt", color: "#f59e0b", bg: "rgba(245,158,11,0.05)", border: "rgba(245,158,11,0.2)", label: "Needs Receipt Fix",    count: 3 },
                { icon: "fa-copy",    color: "#3b82f6", bg: "rgba(59,130,246,0.05)", border: "rgba(59,130,246,0.2)", label: "Duplicate Suspected",  count: 1 },
              ].map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: a.bg, border: `1px solid ${a.border}`, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ color: a.color, fontSize: 12 }} />
                    <span style={{ fontSize: 12, color: C.text }}>{a.label}</span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: a.color }}>{a.count}</span>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Quick Actions</h3>
              {[
                { icon: "fa-pen-to-square", label: "Edit Limits",           onClick: () => setPolicyDrawer({ open: true, employee: null }) },
                { icon: "fa-shield-halved", label: "Assign Policy",         onClick: () => {} },
                { icon: "fa-arrows-rotate", label: "Bulk Update Caps",      onClick: () => {} },
                { icon: "fa-download",      label: "Download Policy Report", onClick: () => {} },
              ].map((a, i) => (
                <button key={i} onClick={a.onClick} style={{ ...s.btnOutline, width: "100%", marginBottom: 8, justifyContent: "flex-start" }}>
                  <i className={`fa-solid ${a.icon}`} style={{ fontSize: 12 }} /> {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── RECENT ACTIVITY ── */}
        <section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, margin: "0 24px 24px", padding: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Recent Activity</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { icon: "fa-check",        iconBg: "rgba(34,197,94,0.1)",  iconColor: "#22c55e", text: <><strong>Sarah</strong> approved Fuel claim <strong style={{ color: C.purple }}>₹850</strong></>,         time: "2 hours ago"  },
              { icon: "fa-xmark",        iconBg: "rgba(239,68,68,0.1)",  iconColor: "#ef4444", text: <><strong>Finance</strong> rejected duplicate bill <strong style={{ color: C.purple }}>₹1,200</strong></>,  time: "5 hours ago"  },
              { icon: "fa-gear",         iconBg: "rgba(59,130,246,0.1)", iconColor: "#3b82f6", text: <><strong>Admin</strong> updated Manager travel limit</>,                                                    time: "Yesterday"    },
              { icon: "fa-file-invoice", iconBg: `rgba(134,96,248,0.1)`, iconColor: C.purple,  text: <>Airport transfer claim submitted by <strong>Arun</strong></>,                                             time: "2 days ago"   },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: item.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                  <i className={`fa-solid ${item.icon}`} style={{ color: item.iconColor, fontSize: 12 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 13, margin: 0 }}>{item.text}</p>
                  <p style={{ fontSize: 11, color: C.muted, margin: "4px 0 0" }}>{item.time}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ── OVERLAYS ── */}
      <ApprovalPopover
        open={popover.open}
        anchorRect={popover.anchorRect}
        amount={popover.amount}
        onConfirm={() => { approveRow(popover.rowId); setPopover(p => ({ ...p, open: false })); }}
        onClose={() => setPopover(p => ({ ...p, open: false }))}
      />

      <BulkModal
        open={bulkModal}
        count={selectedCount}
        totalAmount={selectedTotal}
        onConfirm={handleBulkApprove}
        onClose={() => setBulkModal(false)}
      />

      <PolicyLimitsDrawer
        open={policyDrawer.open}
        employee={policyDrawer.employee}
        onClose={() => setPolicyDrawer({ open: false, employee: null })}
      />

      <AddReimburseDialog
        open={addDialogOpen}
        employee={addPreReimburse}
        reimburseStaff={reimburseStaff}
        onChange={handleReimburseChange}
        onSave={handleSaveReimburse}
        onClose={() => setAddDialogOpen(false)}
      />

      <ToastContainer position="top-right" autoClose={2000} theme="dark" />
    </>
  );
};

export default Reimbursement;